import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import router from '@/javascript/router/router';
import { useDonationStore } from '@/javascript/stores/donation';

export const useDemoLoopStore = defineStore('demoLoop', () => {
  // State
  const active = ref(false);
  const currentStep = ref(0);
  const currentLoop = ref(null);
  const paused = ref(false);
  const narration = ref('');
  const stepCount = ref(0);
  const stepProgress = ref(0);
  const loopContinuously = ref(false);
  const pickerOpen = ref(false);
  const pickerContext = ref(null);

  // Internal
  let _stepGen = 0;
  let _timer = null;
  let _progressInterval = null;
  let _cursorEl = null;
  let _spotlightEl = null;
  let _keyHandler = null;

  // ─── Loops ───────────────────────────────────────────────────────────────────

  const loops = {
    'org-admin': {
      label: '🏢 Org Admin Dashboard Tour',
      description: 'Walk through every section of the organization admin dashboard.',
      context: 'client-dashboard',
      steps: [
        { panel: 'overview', narration: 'Welcome to your AllGive dashboard — a real-time snapshot of your organization\'s fundraising activity.', delay: 7500, action: 'spotlight:#overview-donation-projections-data-card' },
        { panel: 'overview', narration: 'Track donation trends over time — toggle between daily, weekly, monthly, yearly and fiscal views.', delay: 7500, action: 'spotlight:.toggle-switch' },
        { panel: 'overview', narration: 'See how your gifts break down — one-time vs recurring, online vs offline.', delay: 7500, action: 'spotlight-by-title-sequential:Gift Frequency,Gift Type' },
        { panel: 'overview', narration: 'Recent gifts stream in as they happen. Your campaign calendar shows what\'s active and upcoming.', delay: 7500, action: 'scroll-spotlight:.recent-gifts-card' },
        { panel: 'donors', narration: 'Your Donors panel — every person who\'s given to your organization, searchable and filterable.', delay: 7500, action: 'spotlight:.dashboard-model-index' },
        { panel: 'donors', narration: 'Click any donor to see their complete giving history, contact details, and engagement timeline.', delay: 4500, action: 'click-first-row' },
        { panel: 'donors', narration: 'From here you can view every gift, update contact info, or generate a contribution statement.', delay: 2500, action: 'spotlight:.dashboard-model-show' },
        { panel: 'households', narration: 'Households — every donor is automatically placed into their own household. You can reorganize them into family or group households at any time.', delay: 8500, action: 'wait-spotlight:.dashboard-model-index' },
        { panel: 'households', narration: 'Create a new household to group donors together — families, couples, or any custom grouping.', delay: 6500, action: 'spotlight:.btn.btn-outline' },
        { panel: 'households', narration: 'Click into any household to see its members, giving history, and details.', delay: 6000, action: 'click-first-row' },
        { panel: 'households', narration: 'The member list shows each person in the household, their role, and their individual giving.', delay: 7000, action: 'wait-spotlight:.space-y-2' },
        { panel: 'households', narration: 'The map shows the household\'s location based on their address.', delay: 5500, action: 'scroll-spotlight:.grid.grid-cols-1 > div:last-child' },
        { panel: 'households', narration: 'Edit the household details or add new members using these controls.', delay: 6500, action: 'scroll-spotlight:.grid.grid-cols-1 > div:first-child' },
        { panel: 'households', narration: 'Keep internal notes about the household — only visible to organization staff.', delay: 6000, action: 'scroll-spotlight:.space-y-6 > div:nth-child(3)' },
        { panel: 'households', narration: 'Generate a tax year PDF report summarizing the household\'s combined giving for any year.', delay: 6500, action: 'scroll-spotlight:.space-y-6 > div:nth-child(4)' },
        { panel: 'households', narration: 'The donation history shows every gift from all household members in one place.', delay: 6500, action: 'scroll-spotlight:.space-y-6 > div:nth-child(5)' },
        { panel: 'gifts', narration: 'The Gifts panel — every donation tracked with amount, date, donor, fund, and payment status.', delay: 7500, action: 'spotlight:.dashboard-model-index' },
        { panel: 'gifts', narration: 'Import offline donations in bulk via Excel, or export filtered reports for your records.', delay: 7000, action: 'click-el:.donations-index-wrapper .btn-outline:last-of-type' },
        { panel: 'recurring-gifts', narration: 'Recurring gifts — manage active subscriptions, see payment history, and track donor retention.', delay: 7500, action: 'click-el:.tabs-header button:last-of-type' },
        { panel: 'funds', narration: 'Funds let you organize donations by purpose — general operating, building fund, missions, or any category you define.', delay: 7500, action: 'spotlight:.dashboard-model-index' },
        { panel: 'funds', narration: 'Each fund tracks its own donation total and can be assigned to campaigns.', delay: 7500, action: 'click-first-row' },
        { panel: 'campaigns', narration: 'Campaigns — time-bound fundraising drives with goals, progress tracking, and dedicated landing pages.', delay: 7500, action: 'spotlight:.dashboard-model-index' },
        { panel: 'campaigns', narration: 'Each campaign shows real-time progress toward its goal with charts and donor engagement metrics.', delay: 8500, action: 'click-first-row' },
        { panel: 'design-hub', narration: 'The Design Hub — build and customize your donation website with drag-and-drop components.', delay: 7500, action: 'wait-spotlight:.control-bar-container' },
        { panel: 'design-hub', narration: 'Preview on desktop, tablet, and mobile instantly — full branding control with colors, logos, and layouts.', delay: 8500, action: 'click-mobile-preview' },
        { panel: 'payment_processors', narration: 'Payment processors — connect Stripe or Helcim in minutes. Manage transaction fees and payout settings.', delay: 7500, action: null },
        { panel: 'reports', narration: 'Reports — generate donor lists, year-end tax summaries, contribution statements, and donation exports on demand.', delay: 7500, action: 'spotlight:.reports-container' },
        { panel: 'team', narration: 'Team management — invite staff members, assign roles, and control who has access to what.', delay: 7000, action: 'spotlight:.dashboard-model-index' },
        { panel: 'team', narration: 'Organization settings are found in the menu — let\'s take a look.', delay: 5000, action: 'show-menu-to-settings' },
        { panel: 'organization_settings', narration: 'Organization settings — your nonprofit\'s name, address, tax ID, and platform preferences all in one place.', delay: 7000, action: null },
        { panel: 'overview', narration: 'That\'s AllGive — everything your organization needs to manage donations, donors, and campaigns in one place.', delay: 7000, action: null },
      ]
    },
    'donor-portal': {
      label: '🎁 Donor Portal Tour',
      description: 'Walk through the donor-facing dashboard — profile, subscriptions, reports, and giving history.',
      context: 'donor-dashboard',
      steps: [
        { panel: 'overview', narration: 'Welcome to your donor portal — your personal giving hub for this organization.', delay: 7000, action: 'spotlight:.grid > *:first-child' },
        { panel: 'overview', narration: 'Your active subscriptions are here — see amounts, frequency, and manage them anytime.', delay: 7500, action: 'scroll-spotlight:.grid > *:nth-child(2)' },
        { panel: 'overview', narration: 'Download your contribution reports — year-to-date or by specific year for tax purposes.', delay: 7500, action: 'scroll-spotlight:.contrib-cta' },
        { panel: 'overview', narration: 'View a quick summary of your Household — "View Details" will take you to the Household Tab.', delay: 7500, action: 'spotlight-by-title:My Household' },
        { panel: 'overview', narration: 'Your recent giving activity at a glance — every gift with date, amount, and fund.', delay: 7000, action: 'scroll-spotlight:.donor-dashboard-container .grid ~ .data-card-container' },
        { panel: 'overview', narration: 'Click any gift to see the full details — amount, designation, date, and receipt options.', delay: 6000, action: 'click-donation-card' },
        { panel: 'tax', narration: 'The Gifts tab — your complete donation history, all in one place. Click any gift to see the full details.', delay: 7500, action: 'spotlight:.donor-dashboard-container .dashboard-panel-content ul' },
        { panel: 'household', narration: 'Your Household — see who\'s in your household group and their roles.', delay: 7000, action: 'spotlight:.donor-dashboard-container .dashboard-panel-content' },
        { panel: 'household', narration: 'Depending on your household role, you can view combined giving information for your household.', delay: 7000, action: 'spotlight:.donor-dashboard-container .dashboard-panel-content' },
        { panel: 'overview', narration: 'That\'s your donor portal — check in anytime to manage subscriptions, download tax receipts, review your giving history, or household details.', delay: 7000, action: null },
      ]
    }
  };

  const loopKeys = computed(() => Object.keys(loops));
  const loopList = computed(() => Object.entries(loops).map(([key, l]) => ({ key, label: l.label, description: l.description })));
  const currentLoopData = computed(() => currentLoop.value ? loops[currentLoop.value] : null);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function getScrollContainer() {
    return document.querySelector('.dashboard-panel-content');
  }

  function scrollToEl(el) {
    if (!el) return;
    const container = getScrollContainer();
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const scrollTop = container.scrollTop + (elRect.top - containerRect.top) - 100;
      container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function findEl(selector) {
    // Try multiple selectors separated by comma — return first match
    const selectors = selector.split(',');
    for (const s of selectors) {
      const el = document.querySelector(s.trim());
      if (el) return el;
    }
    return null;
  }

  function dismissModals() {
    // Close donation offline modal via store
    try {
      const donationStore = useDonationStore();
      if (donationStore.showOfflineModal) donationStore.showOfflineModal = false;
    } catch (e) { /* store may not be initialized */ }
    // Close any open drawers
    const drawerMask = document.querySelector('.drawer-mask.visible');
    if (drawerMask) drawerMask.click();
    // Close header dropdown menu
    const openMenu = document.querySelector('.dropdown-menu.show');
    if (openMenu) {
      const hamburger = document.querySelector('.hamburger-btn');
      if (hamburger) hamburger.click();
    }
    // Close modals (but not design hub elements);
    const modal = document.querySelector('.base-modal-overlay, .modal-overlay');
    if (modal) {
      const closeBtn = modal.querySelector('.close-btn');
      if (closeBtn) closeBtn.click();
    }
  }

  // ─── Cursor ──────────────────────────────────────────────────────────────────

  let _lastCursorX = null;
  let _lastCursorY = null;

  function ensureCursor() {
    if (_cursorEl && document.body.contains(_cursorEl)) return _cursorEl;
    _cursorEl = document.createElement('div');
    _cursorEl.className = 'demo-cursor';
    _cursorEl.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M5 3l14 8-6 2-3 6-5-16z" fill="#10be5a" stroke="#fff" stroke-width="1.5"/></svg>`;
    const initX = _lastCursorX ?? window.innerWidth / 2;
    const initY = _lastCursorY ?? window.innerHeight / 2;
    Object.assign(_cursorEl.style, {
      position: 'fixed', zIndex: '99999', pointerEvents: 'none',
      top: initY + 'px', left: initX + 'px', transition: 'none', opacity: '0'
    });
    document.body.appendChild(_cursorEl);
    return _cursorEl;
  }

  function removeCursor() {
    if (_cursorEl && _cursorEl.parentNode) _cursorEl.parentNode.removeChild(_cursorEl);
    _cursorEl = null;
  }

  function moveCursorTo(x, y, duration = 800, callback) {
    const cursor = ensureCursor();
    cursor.style.opacity = '1';
    const startX = _lastCursorX ?? parseFloat(cursor.style.left) ?? window.innerWidth / 2;
    const startY = _lastCursorY ?? parseFloat(cursor.style.top) ?? window.innerHeight / 2;
    const dx = x - startX;
    const dy = y - startY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // Perpendicular unit vector for Gerstner displacement
    const perpX = dist > 0 ? -dy / dist : 0;
    const perpY = dist > 0 ? dx / dist : 0;
    // Gerstner wave params — subtle amplitude scaled to travel distance
    const amplitude = Math.min(dist * 0.04, 12);
    const frequency = 2.5;
    const startTime = performance.now();
    const wiggleDuration = 450; // ms for end-settle

    function animate(now) {
      const elapsed = now - startTime;
      const totalDuration = duration + wiggleDuration;
      const t = Math.min(elapsed / totalDuration, 1);

      if (elapsed <= duration) {
        // Transit phase — Gerstner wave displacement
        const progress = elapsed / duration;
        const ease = 1 - Math.pow(1 - progress, 3);
        // Gerstner wave: amplitude fades in then out (peaks mid-transit)
        const envelope = Math.sin(progress * Math.PI);
        const wave = Math.sin(progress * Math.PI * 2 * frequency) * amplitude * envelope;

        cursor.style.left = (startX + dx * ease + perpX * wave) + 'px';
        cursor.style.top = (startY + dy * ease + perpY * wave) + 'px';
      } else {
        // End-settle: slight downward drag then ease back (like finger pressing down)
        const wiggleT = (elapsed - duration) / wiggleDuration;
        const press = Math.sin(wiggleT * Math.PI) * 4 * (1 - wiggleT);

        cursor.style.left = x + 'px';
        cursor.style.top = (y + press) + 'px';
      }

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        cursor.style.left = x + 'px';
        cursor.style.top = y + 'px';
        _lastCursorX = x;
        _lastCursorY = y;
        if (callback) callback();
      }
    }
    requestAnimationFrame(animate);
  }

  function clickRipple(x, y) {
    const ripple = document.createElement('div');
    ripple.className = 'demo-click-ripple';
    Object.assign(ripple.style, {
      position: 'fixed', left: (x - 15) + 'px', top: (y - 15) + 'px',
      width: '30px', height: '30px', borderRadius: '50%',
      border: '2px solid #10be5a', pointerEvents: 'none', zIndex: '99998'
    });
    document.body.appendChild(ripple);
    setTimeout(() => ripple.parentNode?.removeChild(ripple), 600);
  }

  // ─── Spotlight ───────────────────────────────────────────────────────────────

  function showSpotlight(element) {
    clearSpotlight();
    if (!element) return;
    const rect = element.getBoundingClientRect();
    _spotlightEl = document.createElement('div');
    _spotlightEl.className = 'demo-spotlight';
    Object.assign(_spotlightEl.style, {
      position: 'fixed', zIndex: '99990', pointerEvents: 'none',
      left: (rect.left - 6) + 'px', top: (rect.top - 6) + 'px',
      width: (rect.width + 12) + 'px', height: (rect.height + 12) + 'px',
      borderRadius: '8px', border: '3px solid rgba(16, 190, 90, 0.8)',
      boxShadow: '0 0 0 4000px rgba(0,0,0,0.25), 0 0 20px rgba(16, 190, 90, 0.5)',
      animation: 'demo-pulse 2s ease-in-out infinite'
    });
    document.body.appendChild(_spotlightEl);
  }

  function clearSpotlight() {
    document.querySelectorAll('.demo-spotlight').forEach(el => el.parentNode?.removeChild(el));
    _spotlightEl = null;
  }

  function showSpotlightMulti(element) {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    // Clip to viewport so highlights don't extend into off-screen area
    const top = Math.max(rect.top, 0);
    const left = Math.max(rect.left, 0);
    const bottom = Math.min(rect.bottom, window.innerHeight);
    const right = Math.min(rect.right, window.innerWidth);
    const width = right - left;
    const height = bottom - top;
    if (width <= 0 || height <= 0) return; // fully off-screen, skip

    const el = document.createElement('div');
    el.className = 'demo-spotlight';
    Object.assign(el.style, {
      position: 'fixed', zIndex: '99990', pointerEvents: 'none',
      left: (left - 4) + 'px', top: (top - 4) + 'px',
      width: (width + 8) + 'px', height: (height + 8) + 'px',
      borderRadius: '8px', border: '3px solid rgba(16, 190, 90, 0.8)',
      boxShadow: '0 0 20px rgba(16, 190, 90, 0.5)',
      animation: 'none'
    });
    document.body.appendChild(el);
  }

  // ─── Navigation ──────────────────────────────────────────────────────────────

  function navigateToPanel(panel, loopData) {
    const ctx = loopData.context;
    if (ctx === 'donor-dashboard') {
      const org = router.currentRoute.value.params.org;
      router.push({ name: 'donor-dashboard', params: { org, panel } });
    } else {
      const route = router.currentRoute.value;
      if (route.name === 'client-dashboard-scoped') {
        router.push({ name: 'client-dashboard-scoped', params: { panel, id: null } });
      } else {
        const org = route.params.org;
        router.push({ name: 'client-dashboard', params: { panel, org, id: null } });
      }
    }
  }

  // ─── Step Runner ─────────────────────────────────────────────────────────────

  function runStep() {
    const gen = _stepGen;
    const loopData = loops[currentLoop.value];
    if (!loopData) return stop();

    const step = loopData.steps[currentStep.value];
    if (!step) return stop();

    narration.value = step.narration;
    stepCount.value = loopData.steps.length;
    stepProgress.value = 0;
    clearSpotlight();
    removeCursor();
    dismissModals();

    // Navigate if panel changed
    const currentPanel = router.currentRoute.value.params.panel;
    const needsNav = currentPanel !== step.panel;

    if (needsNav) {
      // Animate cursor to the sidebar item before navigating
      const sidebarItems = document.querySelectorAll('.sidebar-item');
      let targetItem = null;

      if (loopData.context === 'donor-dashboard') {
        const tabs = document.querySelectorAll('.segmented__item');
        if (step.panel === 'tax') targetItem = tabs[1];
        else if (step.panel === 'household') targetItem = tabs[2];
        else targetItem = tabs[0];
      } else {
        sidebarItems.forEach(item => {
          const label = item.querySelector('.panel-label');
          if (!label) return;
          const text = label.textContent.trim().toLowerCase();
          const panelMap = {
            'overview': 'overview', 'donors': 'donors', 'my team': 'team',
            'funds': 'funds', 'campaigns': 'campaigns', 'gifts': 'gifts',
            'households': 'households',
            'payment processors': 'payment_processors', 'reporting': 'reports',
            'design hub': 'design-hub', 'organization settings': 'organization_settings'
          };
          Object.entries(panelMap).forEach(([label_text, panel_key]) => {
            if (text === label_text && (panel_key === step.panel || (panel_key === 'gifts' && step.panel === 'recurring-gifts'))) targetItem = item;
          });
        });
      }

      if (targetItem) {
        const rect = targetItem.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        moveCursorTo(x, y, 600, () => {
          if (_stepGen !== gen) return;
          clickRipple(x, y);
          setTimeout(() => {
            if (_stepGen !== gen) return;
            navigateToPanel(step.panel, loopData);
            scheduleAction(step, gen);
          }, 300);
        });
      } else {
        navigateToPanel(step.panel, loopData);
        scheduleAction(step, gen);
      }
    } else {
      // Same panel — scroll container to top first, then do action
      const container = getScrollContainer();
      if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
      scheduleAction(step, gen);
    }
  }

  function scheduleAction(step, gen) {
    // Wait for DOM to render after navigation
    setTimeout(() => {
      if (_stepGen !== gen) return;
      executeAction(step.action, gen);
      startProgress(step.delay, gen);

      _timer = setTimeout(() => {
        if (_stepGen !== gen || paused.value) return;
        advance();
      }, step.delay);
    }, 1000);
  }

  function executeAction(action, gen) {
    if (!action || _stepGen !== gen) return;

    if (action === 'scroll-to-bottom') {
      const container = getScrollContainer();
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    } else if (action.startsWith('click-el:')) {
      const selector = action.replace('click-el:', '');
      const el = findEl(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        moveCursorTo(x, y, 600, () => {
          if (_stepGen !== gen) return;
          clickRipple(x, y);
          el.click();
        });
      }
    } else if (action === 'click-first-row') {
      const row = findEl('.dashboard-panel-content table tbody tr:first-child,.dashboard-panel-content .index-table tbody tr:first-child');
      if (row) {
        const rect = row.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        moveCursorTo(x, y, 700, () => {
          if (_stepGen !== gen) return;
          clickRipple(x, y);
          row.click();
          // Wait for show view to render, then spotlight it
          setTimeout(() => {
            if (_stepGen !== gen) return;
            const showView = findEl('.dashboard-model-show');
            if (showView) showSpotlight(showView);
          }, 800);
        });
      }
    } else if (action === 'click-donation-card') {
      // Find a recurring donation card first, fallback to any card
      const cards = document.querySelectorAll('.donor-dashboard-container .donation-card--clickable');
      let target = null;
      cards.forEach(card => {
        if (!target && card.querySelector('.subscription-pill')) target = card;
      });
      if (!target && cards.length) target = cards[0];
      if (target) {
        const rect = target.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        moveCursorTo(x, y, 600, () => {
          if (_stepGen !== gen) return;
          clickRipple(x, y);
          target.click();
        });
      }
    } else if (action === 'click-mobile-preview') {
      // Click mobile preview, then switch back to desktop
      const buttons = document.querySelectorAll('.control-bar-container [role="button"]');
      const mobileBtn = buttons[2]; // Desktop=0, Tablet=1, Mobile=2
      const desktopBtn = buttons[0];
      if (mobileBtn) {
        const rect = mobileBtn.getBoundingClientRect();
        moveCursorTo(rect.left + rect.width / 2, rect.top + rect.height / 2, 600, () => {
          if (_stepGen !== gen) return;
          clickRipple(rect.left + rect.width / 2, rect.top + rect.height / 2);
          mobileBtn.click();
          // Switch back to desktop after a few seconds
          setTimeout(() => {
            if (_stepGen !== gen) return;
            if (desktopBtn) desktopBtn.click();
          }, 4000);
        });
      }
    } else if (action === 'show-menu-to-settings') {
      // Click hamburger to open menu
      const hamburger = findEl('.hamburger-btn');
      if (hamburger) {
        const rect = hamburger.getBoundingClientRect();
        moveCursorTo(rect.left + rect.width / 2, rect.top + rect.height / 2, 500, () => {
          if (_stepGen !== gen) return;
          clickRipple(rect.left + rect.width / 2, rect.top + rect.height / 2);
          hamburger.click();
          // Wait for menu to open, then find and spotlight the Settings item
          setTimeout(() => {
            if (_stepGen !== gen) return;
            // Find the nav-item that contains "Settings"
            const navItems = document.querySelectorAll('.dropdown-menu .nav-item');
            let settingsItem = null;
            navItems.forEach(item => {
              if (item.textContent.includes('Settings')) settingsItem = item;
            });
            if (settingsItem) showSpotlight(settingsItem);
            // Close menu after a moment
            setTimeout(() => {
              if (_stepGen !== gen) return;
              hamburger.click();
              clearSpotlight();
            }, 3000);
          }, 400);
        });
      }
    } else if (action === 'click-first-accordion') {
      const acc = findEl('.dashboard-panel-content .accordion-header,.dashboard-panel-content .accordion-item:first-of-type');
      if (acc) {
        const rect = acc.getBoundingClientRect();
        moveCursorTo(rect.left + rect.width / 2, rect.top + rect.height / 2, 600, () => {
          if (_stepGen !== gen) return;
          clickRipple(rect.left + rect.width / 2, rect.top + rect.height / 2);
          acc.click();
        });
      }
    } else if (action.startsWith('spotlight:')) {
      const selector = action.replace('spotlight:', '');
      const el = findEl(selector);
      if (el) showSpotlight(el);
    } else if (action.startsWith('wait-spotlight:')) {
      const selector = action.replace('wait-spotlight:', '');
      let attempts = 0;
      const poll = () => {
        if (_stepGen !== gen) return;
        const el = findEl(selector);
        if (el) {
          showSpotlight(el);
        } else if (attempts < 10) {
          attempts++;
          setTimeout(poll, 500);
        }
      };
      poll();
    } else if (action.startsWith('spotlight-by-title-sequential:')) {
      const titles = action.replace('spotlight-by-title-sequential:', '').split(',').map(t => t.trim());
      const cards = document.querySelectorAll('.donut-chart-data-card, .data-card-container');
      const targets = [];
      cards.forEach(card => {
        const titleEl = card.querySelector('.card-title, .title, h3, h2, [class*="title"]');
        if (titleEl && titles.some(t => titleEl.textContent.trim().includes(t))) {
          targets.push(card);
        }
      });
      if (targets.length) {
        let i = 0;
        const showNext = () => {
          if (_stepGen !== gen || i >= targets.length) return;
          clearSpotlight();
          scrollToEl(targets[i]);
          setTimeout(() => {
            if (_stepGen !== gen) return;
            showSpotlight(targets[i]);
            i++;
            if (i < targets.length) setTimeout(showNext, 3500);
          }, 500);
        };
        showNext();
      }
    } else if (action.startsWith('spotlight-by-title:')) {
      const titles = action.replace('spotlight-by-title:', '').split(',').map(t => t.trim());
      const cards = document.querySelectorAll('.donut-chart-data-card, .data-card-container');
      const targets = [];
      cards.forEach(card => {
        const titleEl = card.querySelector('.card-title, .title, h3, h2, [class*="title"]');
        if (titleEl && titles.some(t => titleEl.textContent.trim().includes(t))) {
          targets.push(card);
        }
      });
      if (targets.length) {
        scrollToEl(targets[0]);
        setTimeout(() => {
          if (_stepGen !== gen) return;
          targets.forEach(el => showSpotlightMulti(el));
        }, 600);
      }
    } else if (action.startsWith('spotlight-first-n:')) {
      // Format: spotlight-first-n:.selector:N
      const parts = action.replace('spotlight-first-n:', '').split(':');
      const selector = parts.slice(0, -1).join(':');
      const n = parseInt(parts[parts.length - 1]) || 1;
      const els = document.querySelectorAll(selector);
      const targets = Array.from(els).slice(0, n);
      if (targets.length) {
        scrollToEl(targets[0]);
        setTimeout(() => {
          if (_stepGen !== gen) return;
          targets.forEach(el => showSpotlightMulti(el));
        }, 600);
      }
    } else if (action.startsWith('scroll-spotlight-all:')) {
      const selector = action.replace('scroll-spotlight-all:', '');
      const els = document.querySelectorAll(selector);
      if (els.length) {
        // Scroll to the last element so all are visible
        const lastEl = els[els.length - 1];
        scrollToEl(lastEl);
        setTimeout(() => {
          if (_stepGen !== gen) return;
          els.forEach(el => showSpotlightMulti(el));
        }, 600);
      }
    } else if (action.startsWith('scroll-spotlight:')) {
      const selector = action.replace('scroll-spotlight:', '');
      const el = findEl(selector);
      if (el) {
        scrollToEl(el);
        setTimeout(() => {
          if (_stepGen !== gen) return;
          showSpotlight(el);
        }, 500);
      }
    }
  }

  function startProgress(duration, gen) {
    const startTime = Date.now();
    clearInterval(_progressInterval);
    _progressInterval = setInterval(() => {
      if (_stepGen !== gen) { clearInterval(_progressInterval); return; }
      if (paused.value) return;
      const elapsed = Date.now() - startTime;
      stepProgress.value = Math.min(elapsed / duration, 1);
      if (elapsed >= duration) clearInterval(_progressInterval);
    }, 50);
  }

  function advance() {
    const loopData = loops[currentLoop.value];
    if (!loopData) return stop();

    clearSpotlight();
    removeCursor();

    if (currentStep.value >= loopData.steps.length - 1) {
      if (loopContinuously.value) {
        clearTimeout(_timer);
        _stepGen++;
        currentStep.value = 0;
        runStep();
      } else {
        stop();
      }
    } else {
      currentStep.value++;
      runStep();
    }
  }

  // ─── Controls ────────────────────────────────────────────────────────────────

  function start(loopKey) {
    if (!loops[loopKey]) return;
    _stepGen++;
    active.value = true;
    document.body.classList.add('demo-active');
    currentLoop.value = loopKey;
    currentStep.value = 0;
    paused.value = false;
    pickerOpen.value = false;
    registerKeyboard();
    runStep();
  }

  function stop() {
    _stepGen++;
    active.value = false;
    document.body.classList.remove('demo-active');
    currentLoop.value = null;
    currentStep.value = 0;
    paused.value = false;
    narration.value = '';
    stepProgress.value = 0;
    clearTimeout(_timer);
    clearInterval(_progressInterval);
    clearSpotlight();
    removeCursor();
    _lastCursorX = null;
    _lastCursorY = null;
    unregisterKeyboard();
  }

  function togglePause() {
    paused.value = !paused.value;
    if (!paused.value) {
      _stepGen++;
      runStep();
    } else {
      clearTimeout(_timer);
      clearInterval(_progressInterval);
    }
  }

  function jumpToStep(index) {
    const loopData = loops[currentLoop.value];
    if (!loopData || index < 0 || index >= loopData.steps.length) return;
    _stepGen++;
    clearTimeout(_timer);
    clearInterval(_progressInterval);
    clearSpotlight();
    removeCursor();
    currentStep.value = index;
    paused.value = false;
    runStep();
  }

  // ─── Keyboard ────────────────────────────────────────────────────────────────

  function registerKeyboard() {
    unregisterKeyboard();
    _keyHandler = (e) => {
      if (!active.value) return;
      if (e.code === 'Space') { e.preventDefault(); togglePause(); }
      else if (e.code === 'Escape') { stop(); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); advance(); }
      else if (e.code === 'ArrowLeft') { e.preventDefault(); jumpToStep(Math.max(0, currentStep.value - 1)); }
    };
    window.addEventListener('keydown', _keyHandler);
  }

  function unregisterKeyboard() {
    if (_keyHandler) window.removeEventListener('keydown', _keyHandler);
    _keyHandler = null;
  }

  // Global keyboard listener (Alt+D to open picker);
  function _globalKeyHandler(e) {
    if (e.altKey && e.code === 'KeyD') {
      e.preventDefault();
      e.stopPropagation();
      pickerOpen.value = !pickerOpen.value;
    }
    if (e.altKey && /^Digit[1-9]$/.test(e.code) && !active.value) {
      e.preventDefault();
      const idx = parseInt(e.code.replace('Digit', '')) - 1;
      const keys = Object.keys(loops);
      if (keys[idx]) start(keys[idx]);
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', _globalKeyHandler, true);
  }

  // URL param auto-start
  function checkUrlParam() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tour') === '1') {
      pickerOpen.value = true;
    }
  }

  if (typeof window !== 'undefined') {
    checkUrlParam();
  }

  return {
    active, currentStep, currentLoop, paused, narration,
    stepCount, stepProgress, loopContinuously, pickerOpen, pickerContext,
    loopKeys, loopList, currentLoopData,
    start, stop, togglePause, jumpToStep, loops
  };
});
